/* CASH REPORT — what the three report screens share: the call, the dates and figures as they are
   printed, the report's state chip, and the period bar. One copy, so the Weekly report, the 13-week
   plan and the month-end reconcile cannot come to print one fact three ways. */
import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, FlaskConical } from 'lucide-react';
import { BACKEND_URL } from './backendConfig';

export const API = `${BACKEND_URL}/api/cash`;
/* "TEST AS" (2026-10-09) — the role this browser is playing, kept per viewer and sent with every
   Cash call. The SERVER decides whether to honour it (the Cash Report Admin or Developer, and only
   while Cash mail is held), so a value left in storage the day mail goes live simply does nothing. */
const TEST_KEY = 'cashTestAs';
const testAsNow = () => { try { return localStorage.getItem(TEST_KEY) || ''; } catch { return ''; } };
const setTestAs = (v) => { try { if (v) localStorage.setItem(TEST_KEY, v); else localStorage.removeItem(TEST_KEY); } catch { /* storage off: test as stays off */ } };
export const call = async (url, opts = {}) => {
  const ta = testAsNow();
  const r = await fetch(url, { credentials: 'include', headers: { 'Content-Type': 'application/json', ...(ta ? { 'X-Cash-Test-As': ta } : {}) }, ...opts });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) {
    const e = new Error(j.error || `HTTP ${r.status}`);
    e.blocking = j.blocking;
    throw e;
  }
  return j;
};
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
/* Dates read 09/Oct/2026 — a month NAME, never 09/10, which half the readers of this report would
   take the other way round. Cash stamps are the server's wall clock, printed as stored. */
export const fmtDay = (v) => {
  if (!v) return '';
  const [y, m, d] = String(v).slice(0, 10).split('-');
  return `${d}/${MONTHS[Number(m) - 1]}/${y}`;
};
export const fmtStamp = (v) => (v ? `${fmtDay(v)} ${String(v).slice(11, 16)}` : '');
export const money = (v) => (v === null || v === undefined ? '' : Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
/* THE UNIT IN FRONT OF EVERY AMOUNT — Max, 2026-10-15: "các giá trị tiền cần thêm kí hiệu đơn vị tiền
   trước giá trị nhé". ONE MARK PER CURRENCY, never one mark for two: SGD and USD both write "$" at home,
   and these reports hold both, so S$ and US$ — the way a multi-currency statement writes them. A
   currency not listed prints its code. The minus goes in front of the mark (−S$1.00, not S$-1.00). */
/* VIETNAMESE DONG AS AN INTERNATIONAL REPORT WRITES IT (Max, same day): the ISO code before the figure,
   "VND 100,000" — not ₫ — and no decimals, the dong having no minor unit (ISO 4217). And every mark
   stands one space off its figure, as VND does ("S$ 417,780.21", Max, same day). */
export const CCY_SYMBOL = { SGD: 'S$', USD: 'US$', VND: 'VND', KRW: '₩', JPY: '¥', EUR: '€', GBP: '£', AUD: 'A$', CHF: 'CHF', CNY: 'CN¥', MYR: 'RM', HKD: 'HK$' };
const CCY_DECIMALS = { VND: 0 };
export const ccySym = (c) => { const k = String(c || '').toUpperCase(); return k ? (CCY_SYMBOL[k] ?? k) : ''; };
export const moneyIn = (v, ccy, dpOverride) => {
  if (v === null || v === undefined) return '';
  const dp = dpOverride ?? CCY_DECIMALS[String(ccy || '').toUpperCase()] ?? 2;
  // Rounded first, so a figure that rounds to nothing never prints as a red "−S$ 0".
  const n = dp === 0 ? Math.round(Number(v)) : Number(v);
  const body = Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: dp, maximumFractionDigits: dp });
  const sym = ccySym(ccy);
  return `${n < 0 ? '−' : ''}${sym ? `${sym} ` : ''}${body}`;
};
/* A NEGATIVE AMOUNT IS RED, wherever it stands (Max, 2026-10-15: "giá trị bị âm phải bôi đỏ nha") — not
   only in a change column; a positive change keeps its green. */
/* NO DECIMALS ON THE REPORT (Jay Sung for the Finance team, 2026-10-21: "No need to show decimal"). The
   Cash Report screen wraps its tabs in this; Submit and Approval keep the cents, because there the figures
   are checked against a bank statement line by line. */
export const WholeNumbers = React.createContext(false);
export const Num = ({ v, sign, ccy }) => {
  const whole = React.useContext(WholeNumbers);
  if (v === null || v === undefined) return <span className="pa-muted">—</span>;
  const x = whole ? Math.round(Number(v)) : Number(v);
  const cls = x < 0 ? 'cw-down cw-neg' : (sign && x > 0 ? 'cw-up' : '');
  if (!ccy) return <span className={cls}>{sign && x > 0 ? '+' : ''}{whole ? x.toLocaleString('en-US') : money(x)}</span>;
  return <span className={cls}>{sign && x > 0 ? '+' : ''}{moneyIn(x, ccy, whole ? 0 : undefined)}</span>;
};

/* A BASE CURRENCY TO READ THE REPORT IN (same mail: "Both of original currency and basic currency (KRW, SGD,
   USD) also needs to be shown"; Tuan, 2026-10-22: "the balance can be displayed in basic currencies (SGD /
   USD / KRW)"). Every figure is worked out in SGD; this only DIVIDES it by the week's rate in Set up (SGD per
   one unit, from NetSuite), so the three readings are one figure and cannot disagree. An account's own
   balance stays in its own currency beside it. The choice is the reader's, kept in this browser only. */
export const BASES = ['SGD', 'USD', 'KRW'];
export const readBase = () => { try { const v = localStorage.getItem('cashBase'); return BASES.includes(v) ? v : 'SGD'; } catch { return 'SGD'; } };
export const saveBase = (v) => { try { localStorage.setItem('cashBase', v); } catch { /* nothing to keep it in */ } };
export const toBase = (sgd, rates, base) => {
  if (sgd === null || sgd === undefined) return null;
  if (!base || base === 'SGD') return Number(sgd);
  const r = Number(rates?.[base]);
  return r > 0 ? Number(sgd) / r : null;
};
export function BasePick({ t, value, onChange }) {
  return (
    <div className="cw-basepick" role="group" aria-label={t('cashw.baseLabel')} title={t('cashw.baseWhy')}>
      <span className="cw-basepick-label">{t('cashw.baseLabel')}</span>
      {BASES.map((b) => (
        <button key={b} type="button" className={`cw-basebtn${b === value ? ' on' : ''}`} aria-pressed={b === value} onClick={() => onChange(b)}>{b}</button>
      ))}
    </div>
  );
}
/* The server's JSON limit makes 3.5 MB the largest file that can arrive (MAX_FILE in
   cashReportKit.js); said here before the upload starts rather than after a bare 413. */
export const MAX_FILE = 3.5 * 1024 * 1024;
export const readFile = (file) => new Promise((res, rej) => {
  const r = new FileReader();
  r.onload = () => res(String(r.result).split(',')[1]);
  r.onerror = rej;
  r.readAsDataURL(file);
});
/* The step a report is waiting on, worded as whose move it is — "submitted" says what happened,
   "With the Reviewer" says who has to act, which is what somebody scanning ten entities wants. */
export const stateKey = (row) => (row.status === 'open' && !row.version ? 'none' : row.status);
export const StateChip = ({ t, row }) => <span className={`cw-state s-${stateKey(row)}`}>{t(`cashw.st_${stateKey(row)}`)}</span>;
export const lastStamp = (stamps) => {
  if (!stamps) return null;
  const order = ['closed', 'approved', 'reviewed', 'submitted', 'returned'];
  return order.map((k) => [k, ...(stamps[k] || [])]).filter(([, by, at]) => by && at)
    .sort((a, b) => String(b[2]).localeCompare(String(a[2])))[0] || null;
};
export const LastStamp = ({ t, stamps }) => {
  const ls = lastStamp(stamps);
  return ls ? <div className="cw-sub">{t(`cashw.by_${ls[0]}`, { by: ls[1], at: fmtStamp(ls[2]) })}</div> : null;
};

/* The period bar: previous, the list, next, and one line saying what the period is. `lead` is
   drawn in front of them — Cash Report Import puts the entity there, the other half of "which
   report am I on". */
/* The switch itself, on the four Cash screens — drawn only where the server says this reader may
   test (`me.canTestAs`). Lit amber while on, so a screen played as somebody else never passes for
   the reader's own. Changing it reloads the page: every panel on it was loaded as the old role. */
export function TestAsBar({ t, me }) {
  if (!me?.canTestAs) return null;
  const cur = me.testAs || '';
  return (
    <div className={`cw-testas${cur ? ' on' : ''}`}>
      <FlaskConical size={14} /> <b>{t('cash.testAs')}</b>
      <select value={cur} aria-label={t('cash.testAs')} onChange={(e) => { setTestAs(e.target.value); window.location.reload(); }}>
        <option value="">{t('cash.testOff')}</option>
        {(me.testRoles || []).map((r) => <option key={r} value={r}>{r}</option>)}
      </select>
      <span>{cur ? t('cash.testOnNote', { role: cur }) : t('cash.testOffNote')}</span>
    </div>
  );
}

/* REPORTS WAITING ON THIS READER (Max, 2026-10-09). The server lists, per period, the entities
   whose report waits on THIS reader's decision (`me.waiting`, the same rule that draws the
   buttons), and the rows of the report screens carry a tag saying which ones. The screen used to
   open the first of them in a panel under its table; the panel left the report screens on
   2026-10-15, so the tag is now the way to the decision. */
/* "Waiting for you" on a report screen, which no longer carries the decision (2026-10-15): the tag
   itself opens that report in Cash Report Approval, where it is decided. */
export function WaitingTag({ t, data, entity }) {
  if (!(data?.me?.waiting || []).includes(entity)) return null;
  const q = new URLSearchParams({ type: data.type || '', entity, period: data.period?.code || '' });
  return <Link className="cw-waiting" to={`/cash/approval?${q}`} title={t('cashw.waitingOpen')}>{t('cashw.waitingYou')}</Link>;
}

export function PeriodBar({ t, periods, period, onPick, children, lead = null, trail = null }) {
  const idx = periods.findIndex((p) => p.code === period.code);
  return (
    <div className="cs-band cw-weekbar">
      {lead}
      <button type="button" className="pa-btn" disabled={idx <= 0} onClick={() => onPick(periods[idx - 1].code)} aria-label={t('cashw.prevWeek')}><ChevronLeft size={14} /></button>
      <select value={period.code} onChange={(e) => onPick(e.target.value)}>
        {periods.map((p) => (
          <option key={p.code} value={p.code}>
            {p.label}{p.label !== p.code ? '' : ` · ${fmtDay(p.end)}`}{p.open ? '' : ` · ${p.status === 'Not ended' ? t('cashm.notEnded') : t('cashw.closedWeek')}`}
          </option>
        ))}
      </select>
      <button type="button" className="pa-btn" disabled={idx < 0 || idx >= periods.length - 1} onClick={() => onPick(periods[idx + 1].code)} aria-label={t('cashw.nextWeek')}><ChevronRight size={14} /></button>
      <div className="cs-band-text">{children}</div>
      {trail}
    </div>
  );
}

/* PICK ENTITIES — several at once (Max, 2026-10-15: "filter dropdown gồm Group và các entity nhé cho
   multichoice"). Nothing ticked IS the Group, every entity; ticking GROUP clears the entities, and
   unticking the last entity goes back to it. The ticked list keeps the screen's order of entities. */
export function EntityPick({ t, entities, value, onChange }) {
  const ref = useRef(null);
  const sel = entities.map((e) => e.entity).filter((c) => value.includes(c));
  const isGroup = sel.length === 0;
  const summary = isGroup ? t('cashp.pickGroup') : sel.length <= 3 ? sel.join(', ') : `${sel.slice(0, 3).join(', ')} +${sel.length - 3}`;
  useEffect(() => {
    const onDoc = (e) => { if (ref.current && ref.current.open && !ref.current.contains(e.target)) ref.current.removeAttribute('open'); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);
  const toggle = (code) => onChange(sel.includes(code) ? sel.filter((x) => x !== code) : entities.map((e) => e.entity).filter((c) => c === code || sel.includes(c)));
  return (
    <details className="cw-pick" ref={ref}>
      <summary title={summary}><span className="cw-pick-label">{t('cashp.pickLabel')}</span> <b>{summary}</b></summary>
      <div className="cw-pick-panel">
        <label className="cw-pick-row cw-pick-group">
          <input type="checkbox" checked={isGroup} onChange={() => onChange([])} /> <b>GROUP</b> <span className="pa-muted">{t('cashp.pickGroupWhy')}</span>
        </label>
        {entities.map((e) => (
          <label key={e.entity} className="cw-pick-row">
            <input type="checkbox" checked={sel.includes(e.entity)} onChange={() => toggle(e.entity)} /> <b>{e.entity}</b> <span className="pa-muted">{e.name}</span>
          </label>
        ))}
      </div>
    </details>
  );
}
